<template>
    <BaseDialog
        :showDialog="showDialog"
        :close="closeDialog"
        :headline="$t('lang_PUBLISHING_INCOME_CAPTION_AI_ANALYSIS')"
        maxDialogWidth="700"
        maxCardHeight="600"
        :alvqa="alvqa"
    >
        <template v-slot:dialogText>
            <v-container fluid>
                <v-row v-if="loading" justify="center" align="center" class="py-6">
                    <v-progress-circular indeterminate color="primary" class="mr-3" />
                    <span :alvqa="alvqa + '-loading'">{{ $t('lang_GENERIC_CAPTION_LOADING') }}</span>
                </v-row>

                <v-row v-else-if="error">
                    <v-col cols="12">
                        <div class="error--text" :alvqa="alvqa + '-error'">
                            {{ $alvPlugin.isLangConst(error) ? $t(error) : error }}
                        </div>
                    </v-col>
                </v-row>

                <v-row v-else>
                    <v-col cols="12">
                        <div
                            class="ai-analysis-text"
                            :alvqa="alvqa + '-result'"
                            v-html="renderedResult"
                        ></div>
                    </v-col>
                </v-row>
            </v-container>
        </template>

        <template v-slot:dialogActions>
            <v-container fluid>
                <v-row>
                    <v-spacer />
                    <v-col class="text-right">
                        <BaseButton
                            label="lang_GENERAL_BUTTON_CLOSE"
                            :alvqa="alvqa + '-button-close'"
                            @click="closeDialog"
                        />
                    </v-col>
                </v-row>
            </v-container>
        </template>
    </BaseDialog>
</template>

<script>
import BaseDialog from '@/components/_base/BaseDialog'
import BaseButton from '@/components/_buttons/BaseButton'

export default {
    name: 'IncomeErrorAiAnalysisDialog',
    components: { BaseDialog, BaseButton },
    props: {
        alvqa: {
            type: String,
            default: 'publishing-income-dialog-ai-analysis'
        }
    },
    computed: {
        showDialog() {
            return this.$store.getters.getShowIncomeErrorAiAnalysisDialog
        },
        loading() {
            return this.$store.getters.getIncomeErrorAiAnalysisLoading
        },
        result() {
            return this.$store.getters.getIncomeErrorAiAnalysisResult
        },
        error() {
            return this.$store.getters.getIncomeErrorAiAnalysisError
        },
        renderedResult() {
            return this.renderMarkdown(this.stripExtraFormatting(this.result))
        }
    },
    methods: {
        closeDialog() {
            this.$store.commit('setShowIncomeErrorAiAnalysisDialog', false)
            this.$store.commit('setIncomeErrorAiAnalysisResult', '')
            this.$store.commit('setIncomeErrorAiAnalysisError', '')
        },
        // TEMPORARY: keep only bold + paragraphs from BE response.
        // Headings -> bold, list items -> inline comma-joined.
        stripExtraFormatting(text) {
            if (!text) return ''
            let s = String(text)
                .replace(/^#{1,6}\s+(.+)$/gm, '**$1**')
                .replace(/^\s*>\s?/gm, '')

            const lines = s.split('\n')
            const out = []
            let buf = []
            const flushBuf = () => {
                if (buf.length) {
                    out.push(buf.join(', '))
                    buf = []
                }
            }
            for (const line of lines) {
                const m = /^\s*(?:[-*]|\d+\.)\s+(.+)$/.exec(line)
                if (m) {
                    buf.push(m[1].trim())
                } else {
                    flushBuf()
                    out.push(line)
                }
            }
            flushBuf()
            return out.join('\n')
        },
        renderMarkdown(text) {
            if (!text) return ''
            let html = String(text)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')

            html = html.replace(/^######\s+(.+)$/gm, '<h6>$1</h6>')
            html = html.replace(/^#####\s+(.+)$/gm, '<h5>$1</h5>')
            html = html.replace(/^####\s+(.+)$/gm, '<h4>$1</h4>')
            html = html.replace(/^###\s+(.+)$/gm, '<h3>$1</h3>')
            html = html.replace(/^##\s+(.+)$/gm, '<h2>$1</h2>')
            html = html.replace(/^#\s+(.+)$/gm, '<h1>$1</h1>')

            html = html.replace(/\*\*([^*\n]+?)\*\*/g, '<strong>$1</strong>')
            html = html.replace(/__([^_\n]+?)__/g, '<strong>$1</strong>')
            html = html.replace(/(^|[^*])\*([^*\n]+?)\*(?!\*)/g, '$1<em>$2</em>')
            html = html.replace(/`([^`\n]+?)`/g, '<code>$1</code>')

            html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')

            const lines = html.split('\n')
            const out = []
            let inUl = false
            let inOl = false
            for (let line of lines) {
                const ulMatch = /^\s*[-*]\s+(.+)$/.exec(line)
                const olMatch = /^\s*\d+\.\s+(.+)$/.exec(line)
                if (ulMatch) {
                    if (inOl) { out.push('</ol>'); inOl = false }
                    if (!inUl) { out.push('<ul>'); inUl = true }
                    out.push('<li>' + ulMatch[1] + '</li>')
                } else if (olMatch) {
                    if (inUl) { out.push('</ul>'); inUl = false }
                    if (!inOl) { out.push('<ol>'); inOl = true }
                    out.push('<li>' + olMatch[1] + '</li>')
                } else {
                    if (inUl) { out.push('</ul>'); inUl = false }
                    if (inOl) { out.push('</ol>'); inOl = false }
                    out.push(line)
                }
            }
            if (inUl) out.push('</ul>')
            if (inOl) out.push('</ol>')
            html = out.join('\n')

            html = html.replace(/\n{2,}/g, '</p><p>')
            html = html.replace(/\n/g, '<br>')
            html = '<p>' + html + '</p>'
            html = html.replace(/<p>(\s*<(h[1-6]|ul|ol|li)>)/g, '$1')
            html = html.replace(/(<\/(h[1-6]|ul|ol|li)>\s*)<\/p>/g, '$1')

            return html
        }
    }
}
</script>

<style scoped>
.ai-analysis-text {
    word-break: break-word;
    line-height: 1.5;
}
.ai-analysis-text ::v-deep p {
    margin-bottom: 0.75em;
}
.ai-analysis-text ::v-deep p:last-child {
    margin-bottom: 0;
}
.ai-analysis-text ::v-deep ul,
.ai-analysis-text ::v-deep ol {
    margin: 0.25em 0 0.75em 1.25em;
}
.ai-analysis-text ::v-deep li {
    margin-bottom: 0.25em;
}
.ai-analysis-text ::v-deep h1,
.ai-analysis-text ::v-deep h2,
.ai-analysis-text ::v-deep h3,
.ai-analysis-text ::v-deep h4 {
    font-weight: 600;
    margin: 0.5em 0 0.25em;
}
.ai-analysis-text ::v-deep code {
    background: rgba(0, 0, 0, 0.06);
    padding: 0 4px;
    border-radius: 3px;
    font-size: 0.9em;
}
</style>
